import { addMonths, addDays, subDays } from 'date-fns';
import { computed, observable, set } from 'mobx';
import { bind } from './Bind';
import { toTimezoneAndFormat } from './Date';
import { Enums } from './Enums';

export class Contract implements Mri.Contracts.Contract {
    @computed
    public get isTermBased() {
        switch (this.serviceLevel) {
            case Mri.Contracts.ServiceLevel.SmartVuAdvanced:
            case Mri.Contracts.ServiceLevel.SmartVuCustom:
            case Mri.Contracts.ServiceLevel.Demo:
            case Mri.Contracts.ServiceLevel.OutOfWarranty:
                return false;
            case Mri.Contracts.ServiceLevel.SmartVuDepot:
            case Mri.Contracts.ServiceLevel.MRIStandardWarranty:
            case Mri.Contracts.ServiceLevel.Warranty:
            case Mri.Contracts.ServiceLevel.SmartVuPortal:
            case Mri.Contracts.ServiceLevel.SmartVuGold:
            case Mri.Contracts.ServiceLevel.SmartVuPlatinum:
                return true;
            default:
                return false;
        }
    }
    public constructor(model: Mri.Contracts.Contract) {
        set(this, model);
    }

    @bind
    public contractStatusMessage(displayShipmentDate?: Date | undefined) {
        if (this.inWarranty(displayShipmentDate)) {
            if (!this.active) return 'Inactive';
            else if (this.isNew(displayShipmentDate)) return 'Active: New!';
            else if (this.expiringSoon(displayShipmentDate)) return 'Active: Expiring Soon';
            else return 'Active';
        } else {
            if (this.expiredRecently(displayShipmentDate)) return 'Expired Recently';
            else return 'Expired';
        }
    }

    @bind
    public inWarranty(displayShipmentDate?: Date | undefined) {
        const today = new Date();

        const contractStartDate = this.contractStartDate(displayShipmentDate);
        const contractEndDate = this.contractEndDate(displayShipmentDate);

        return contractStartDate && contractEndDate ? contractStartDate <= today && contractEndDate >= today : false;
    }

    @bind
    public contractStartDate(displayShipmentDate?: Date) {
        return this.startDate ? this.startDate : this.startDate ?? displayShipmentDate ?? this.firstShipmentDate;
    }

    @bind
    public contractEndDate(displayShipmentDate?: Date) {
        const startDate = this.contractStartDate(displayShipmentDate);

        if (!startDate) return;

        return this.isTermBased && this.term && !this.endDate
            ? subDays(addMonths(startDate, this.term), 1)
            : this.endDate;
    }

    @bind
    public isNew(displayShipmentDate?: Date) {
        if (!this.inWarranty(displayShipmentDate)) return false;

        const now = new Date();
        const contractStartDate = this.contractStartDate(displayShipmentDate);
        return contractStartDate && contractStartDate >= subDays(now, 10) && contractStartDate <= addDays(now, 10);
    }

    @bind
    public formatStartDate(displayShipmentDate?: Date) {
        const startDate = this.contractStartDate(displayShipmentDate);

        return startDate ? toTimezoneAndFormat(startDate, 'MMMM d, yyyy', 'UTC') : '-';
    }

    @bind
    public expiringSoon(displayShipmentDate?: Date) {
        if (!this.inWarranty(displayShipmentDate)) return false;

        const now = new Date();
        const contractEndDate = this.contractEndDate(displayShipmentDate);
        return contractEndDate && contractEndDate >= now && contractEndDate <= addDays(now, 30);
    }

    @bind
    public expiredRecently(displayShipmentDate?: Date) {
        const now = new Date();
        const contractEndDate = this.contractEndDate(displayShipmentDate);
        return contractEndDate && contractEndDate > subDays(now, 30) && contractEndDate < now;
    }

    @bind
    public formatEndDate(displayShipmentDate?: Date) {
        const endDate = this.contractEndDate(displayShipmentDate);

        if (!endDate) return '-';

        const formatted = toTimezoneAndFormat(endDate, 'MMMM d, yyyy', 'UTC');
        const prefix = this.inWarranty(displayShipmentDate) ? 'Ends' : 'Ended';
        return `${prefix} ${formatted}`;
    }

    @computed
    public get hasManagedServices() {
        return this.managedServices || this.monitoringManagedServices;
    }

    @computed
    public get serviceLevelType() {
        return Enums.toString(Enums.ServiceLevel, this.serviceLevel);
    }

    @bind
    public expiringWithin30Days(displayShipmentDate?: Date) {
        if (!this.inWarranty(displayShipmentDate)) return false;

        const now = new Date();
        const contractEndDate = this.contractEndDate(displayShipmentDate);
        return contractEndDate && contractEndDate >= now && contractEndDate <= addDays(now, 30);
    }

    @bind
    public expiringWithin60Days(displayShipmentDate?: Date) {
        if (!this.inWarranty(displayShipmentDate)) return false;

        const now = new Date();
        const contractEndDate = this.contractEndDate(displayShipmentDate);
        return contractEndDate && contractEndDate >= now && contractEndDate <= addDays(now, 60);
    }

    @bind
    public expiringWithin90Days(displayShipmentDate?: Date) {
        if (!this.inWarranty(displayShipmentDate)) return false;

        const now = new Date();
        const contractEndDate = this.contractEndDate(displayShipmentDate);
        return contractEndDate && contractEndDate >= now && contractEndDate <= addDays(now, 90);
    }
    

    @observable public active!: boolean;
    @observable public contractNumber!: string;
    @observable public accountNumber!: string;
    @observable public startDate?: Date;
    @observable public endDate?: Date;
    @observable public term!: number;
    @observable public fieldServicesProvider?: string | null;
    @observable public contractNotes?: string | null;
    @observable public serviceCO!: string;
    @observable public serviceLevel!: Mri.Contracts.ServiceLevel;
    @observable public advancedMonitoring!: boolean;
    @observable public api!: boolean;
    @observable public managedServices!: boolean;
    @observable public onsiteFieldServices!: boolean;
    @observable public m2mDevice!: boolean;
    @observable public proofOfPlay!: boolean;
    @observable public customerOwnedParts!: boolean;
    @observable public eventManagement!: boolean;
    @observable public demo!: boolean;
    @observable public coveredProduct!: string;
    @observable public warranty!: string;
    @observable public technicalSupport!: string;
    @observable public fruReplacements!: string;
    @observable public nonFruReplacements!: string;
    @observable public firmwareUpdates!: boolean;
    @observable public customerPortal!: boolean;
    @observable public monitoringManagedServices!: boolean;
    @observable public advancedWarrantyAlerts!: boolean;
    @observable public firstShipmentDate?: Date;
    @observable public lastShipmentDate?: Date;
    public allowPortalAccess!: boolean; // Do not use
    public allowApiAccess!: boolean; // Do not use
}
